// ---------------------------------------------------------------------------
// PB-T.25: Chat Widget — Server-Side Renderer
// ---------------------------------------------------------------------------
// Renders a published-page visitor messaging bubble backed by the mounted
// page-serve -> api-pages -> inbox handoff. Agent refresh still rides
// realtime-hub notifications from the durable inbox write.
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import { attr, jsString, text } from "../escape.js";
import type { RenderContext } from "../types.js";

interface ChatWidgetProps {
  id: string;
  greeting: string;
  placeholder: string;
  position: "br" | "bl";
  aiEnabled: string;
  offlineMessage: string;
  collectName: string;
  accentColor: string;
}

// PB-Q / SEC-O option-b: when page-serve flips
// `TURNSTILE_ENFORCE_PUBLIC_WRITES = "1"`, every POST to `/_ease/chat/messages`
// must carry a `cf-turnstile-response` token. We surface the Cloudflare
// Turnstile widget at render time and gate submit on the visitor solving it.
// PB-Q P1 fix: use the explicit-render flavor so each widget on the page can
// own its callback. Loading the implicit api.js fights with other surfaces
// (blog inline comments) that also bind their callback globally.
const TURNSTILE_API_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function render(props: ChatWidgetProps, ctx: RenderContext): string {
  const position = props.position === "bl" ? "left:24px" : "right:24px";
  const greeting = text(props.greeting || "Hi! How can we help?");
  const placeholder = attr(props.placeholder || "Type a message...");
  const offlineMsg = text(props.offlineMessage || "We'll get back to you soon.");
  const rawSiteKey = typeof ctx.turnstileSiteKey === "string" ? ctx.turnstileSiteKey.trim() : "";
  const turnstileEnabled = rawSiteKey.length > 0;
  const turnstileSiteKey = turnstileEnabled ? attr(rawSiteKey) : "";
  const turnstileSiteKeyJs = turnstileEnabled ? jsString(rawSiteKey) : "";
  // Sanitize accent color: only allow CSS color values (hex, rgb, hsl, var())
  const rawAccent = props.accentColor || "var(--color-primary)";
  const accent = /^(#[0-9a-fA-F]{3,8}|var\(--[\w-]+\)|rgb\(\d+,\s*\d+,\s*\d+\)|hsl\(\d+,\s*\d+%?,\s*\d+%?\))$/.test(rawAccent)
    ? rawAccent
    : "var(--color-primary)";
  const aiLabel = props.aiEnabled === "yes"
    ? `<span style="font-size:0.72rem;opacity:0.86;margin-top:4px;display:block">AI assist can draft from the staff inbox after this message lands.</span>`
    : "";
  const showName = props.collectName === "yes";

  const widgetScript = `
(function(){
  var btn=document.getElementById('ease-chat-btn');
  var panel=document.getElementById('ease-chat-panel');
  var form=document.getElementById('ease-chat-form');
  var input=document.getElementById('ease-chat-input');
  var nameInput=document.getElementById('ease-chat-name');
  var log=document.getElementById('ease-chat-log');
  var status=document.getElementById('ease-chat-status');
  var submit=document.getElementById('ease-chat-submit');
  var meta={tenantId:'${jsString(ctx.tenantId)}',pageId:'${jsString(ctx.pageId)}',versionId:'${jsString(ctx.versionId)}'};
  var storageKey='ease_chat_visitor_id:'+meta.tenantId+':'+meta.pageId;
  var isOpen=false;
  var turnstileSiteKey=${turnstileEnabled ? `'${turnstileSiteKeyJs}'` : "''"};
  var turnstileToken='';
  var turnstileWidgetId=null;
  var turnstileTokenInput=document.getElementById('ease-chat-turnstile-token');
  var turnstileContainer=document.getElementById('ease-chat-turnstile-container');
  // Disable submit until Turnstile resolves a token. When the widget is not
  // wired (no site key bound on page-serve), keep submit enabled so existing
  // visitor chat behavior is preserved for tenants without Turnstile.
  if(submit&&turnstileSiteKey){submit.disabled=true;}
  // PB-Q P1 fix: use Turnstile explicit-render API so the callback is scoped
  // to THIS widget instance. Sharing a global window-level success callback
  // collided with the inline-comments panel when both surfaces rendered on
  // the same page (last load wins).
  function easeChatRenderTurnstile(){
    if(!turnstileSiteKey||!turnstileContainer)return;
    if(!window.turnstile||typeof window.turnstile.render!=='function')return;
    if(turnstileWidgetId!==null)return;
    try{
      turnstileWidgetId=window.turnstile.render(turnstileContainer,{
        sitekey:turnstileSiteKey,
        callback:function(token){
          turnstileToken=String(token||'');
          if(turnstileTokenInput){turnstileTokenInput.value=turnstileToken;}
          if(submit){submit.disabled=!turnstileToken;}
        },
        'error-callback':function(){
          turnstileToken='';
          if(turnstileTokenInput){turnstileTokenInput.value='';}
          if(submit){submit.disabled=true;}
        },
        'expired-callback':function(){
          turnstileToken='';
          if(turnstileTokenInput){turnstileTokenInput.value='';}
          if(submit){submit.disabled=true;}
        }
      });
    }catch{}
  }
  if(turnstileSiteKey){
    if(window.turnstile&&typeof window.turnstile.render==='function'){
      easeChatRenderTurnstile();
    }else{
      // Cloudflare api.js dispatches turnstile.ready once loaded; fall back
      // to a short poll for the rare case where it loads before this script.
      var __easeChatTurnstileWait=setInterval(function(){
        if(window.turnstile&&typeof window.turnstile.render==='function'){
          clearInterval(__easeChatTurnstileWait);
          easeChatRenderTurnstile();
        }
      },150);
    }
  }

  function uuid(){
    if(window.crypto&&typeof window.crypto.randomUUID==='function')return window.crypto.randomUUID();
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g,function(c){
      return (c ^ window.crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16);
    });
  }

  function getVisitorId(){
    try{
      var existing=window.localStorage.getItem(storageKey);
      if(existing)return existing;
      var next=uuid();
      window.localStorage.setItem(storageKey,next);
      return next;
    }catch{
      return uuid();
    }
  }

  function rememberVisitorId(visitorId){
    if(!visitorId)return;
    try{window.localStorage.setItem(storageKey,visitorId);}catch{}
  }

  function appendMessage(kind,body){
    var row=document.createElement('div');
    row.style.display='flex';
    row.style.justifyContent=kind==='visitor'?'flex-end':'flex-start';
    var bubble=document.createElement('div');
    bubble.textContent=body;
    bubble.style.maxWidth='82%';
    bubble.style.borderRadius=kind==='visitor'?'14px 14px 4px 14px':'14px 14px 14px 4px';
    bubble.style.padding='10px 12px';
    bubble.style.fontSize='0.85rem';
    bubble.style.lineHeight='1.45';
    bubble.style.background=kind==='visitor'?'${accent}':'#f0f0f0';
    bubble.style.color=kind==='visitor'?'#fff':'#222';
    row.appendChild(bubble);
    log.appendChild(row);
    log.scrollTop=log.scrollHeight;
  }

  function setStatus(message,isError){
    status.textContent=message;
    status.style.color=isError?'#fee2e2':'rgba(255,255,255,0.82)';
  }

  btn.onclick=function(){
    isOpen=!isOpen;
    panel.style.display=isOpen?'flex':'none';
    btn.textContent=isOpen?'x':'?';
    if(isOpen&&input)input.focus();
  };

  form.onsubmit=function(event){
    event.preventDefault();
    var body=(input.value||'').trim();
    if(!body)return;
    var visitorId=getVisitorId();
    var clientMessageId=uuid();
    appendMessage('visitor',body);
    input.value='';
    submit.disabled=true;
    setStatus('Sending to the team...',false);
    fetch('/_ease/chat/messages',{
      method:'POST',
      headers:{'content-type':'application/json','accept':'application/json'},
      credentials:'same-origin',
      body:JSON.stringify({
        tenantId:meta.tenantId,
        pageId:meta.pageId,
        versionId:meta.versionId,
        visitorId:visitorId,
        clientMessageId:clientMessageId,
        visitorName:nameInput?(nameInput.value||'').trim()||undefined:undefined,
        body:body,
        pageUrl:location.pathname+location.search,
        'cf-turnstile-response':turnstileToken||undefined
      })
    }).then(function(response){
      return response.json().catch(function(){return {};}).then(function(data){
        if(!response.ok)throw new Error(data.message||'Message failed');
        rememberVisitorId(data.visitorId||visitorId);
        setStatus('Message sent. The team can reply from the inbox.',false);
      });
    }).catch(function(){
      appendMessage('agent','${jsString(props.offlineMessage || "We'll get back to you soon.")}');
      setStatus('Could not send right now. Please try again.',true);
    }).finally(function(){
      // Turnstile tokens are single-use. Reset after submit so the widget
      // (when wired) issues a fresh challenge before the next message.
      turnstileToken='';
      if(turnstileTokenInput){turnstileTokenInput.value='';}
      if(turnstileSiteKey&&window.turnstile&&typeof window.turnstile.reset==='function'){
        try{
          if(turnstileWidgetId!==null){window.turnstile.reset(turnstileWidgetId);}
          else{window.turnstile.reset();}
        }catch{}
      }
      submit.disabled=!!turnstileSiteKey;
      input.focus();
    });
  };
})();`;

  return `
<!-- Ease visitor chat surface: native published-page chat handoff -->
<div id="ease-chat-widget" data-ease-chat-surface="native-live-chat" style="position:fixed;bottom:24px;${position};z-index:9999;font-family:system-ui,-apple-system,sans-serif">
  <div id="ease-chat-panel" style="display:none;flex-direction:column;width:340px;height:460px;border-radius:16px;overflow:hidden;box-shadow:0 8px 30px rgba(0,0,0,0.12);border:1px solid #e5e5e5;background:#fff;margin-bottom:12px">
    <div style="background:${accent};color:#fff;padding:16px">
      <div style="font-size:0.72rem;letter-spacing:0.08em;text-transform:uppercase;opacity:0.8">Visitor messaging</div>
      <div style="font-weight:600;font-size:0.95rem;margin-top:4px">Chat with us</div>
      ${aiLabel}
      <div id="ease-chat-status" style="font-size:0.72rem;margin-top:6px;color:rgba(255,255,255,0.82)">Messages go directly to the team inbox.</div>
    </div>
    <div id="ease-chat-log" style="flex:1;overflow:auto;padding:16px;background:#fafafa;display:flex;flex-direction:column;gap:12px">
      <div style="background:#f0f0f0;border-radius:12px 12px 12px 4px;padding:10px 14px;max-width:80%;font-size:0.85rem;color:#333">${greeting}</div>
    </div>
    <form id="ease-chat-form" style="padding:12px;display:flex;flex-direction:column;gap:8px;border-top:1px solid #e5e5e5">
      ${showName ? `<input id="ease-chat-name" type="text" autocomplete="name" placeholder="Your name" style="border:1px solid #ddd;border-radius:10px;padding:9px 10px;font-size:0.84rem" />` : ""}
      <textarea id="ease-chat-input" rows="2" placeholder="${placeholder}" style="resize:none;border:1px solid #ddd;border-radius:10px;padding:9px 10px;font-size:0.84rem;line-height:1.4"></textarea>
      ${turnstileEnabled ? `<div id="ease-chat-turnstile-container" data-ease-turnstile-widget="chat"></div>
      <input type="hidden" id="ease-chat-turnstile-token" name="cf-turnstile-response" value="" />` : ""}
      <div style="display:flex;align-items:center;justify-content:space-between;gap:10px">
        <span id="ease-chat-offline" style="font-size:0.72rem;color:#666;line-height:1.35">${offlineMsg}</span>
        <button id="ease-chat-submit" type="submit" style="border:none;border-radius:999px;background:${accent};color:#fff;font-weight:700;padding:9px 14px;cursor:pointer">Send</button>
      </div>
    </form>
  </div>
  <button id="ease-chat-btn" style="width:56px;height:56px;border-radius:50%;background:${accent};color:#fff;border:none;cursor:pointer;font-size:1.5rem;box-shadow:0 4px 12px rgba(0,0,0,0.15);display:flex;align-items:center;justify-content:center;${props.position === "bl" ? "" : "margin-left:auto"}">?</button>
</div>
<script>${widgetScript}</script>${turnstileEnabled ? `
<script async defer src="${TURNSTILE_API_URL}"></script>` : ""}`;
}

registerRenderer("chat-widget", { render: (props, context) => render(props as unknown as ChatWidgetProps, context) });
